import React, { useMemo, useState } from "react";
import Header from "../../layouts/Header";
import { Button, Form, Table, Badge } from "react-bootstrap";
import { ArrowLeft } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

const SalesMatrixReport = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");

  const categories = {
    "Food": ["Burger", "Pizza", "Pasta", "Sandwich"],
    "Beverages": ["Coffee", "Tea", "Mocktail", "Juice"],
    "Desserts": ["Brownie", "Ice Cream", "Cheesecake", "Waffle"],
    "Combos": ["Meal Combo", "Family Combo", "Party Box"]
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const days = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
  ];

  const getQuarter = (monthIndex) => {
    if (monthIndex <= 2) return "Q1";
    if (monthIndex <= 5) return "Q2";
    if (monthIndex <= 8) return "Q3";
    return "Q4";
  };

  const formatCurrency = (value) =>
    `₹ ${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toggleExpand = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const dummyRows = useMemo(() => {
    const rows = [];

    for (let year = 2024; year <= 2026; year++) {
      months.forEach((month, monthIndex) => {
        const totalDays = new Date(year, monthIndex + 1, 0).getDate();

        for (let date = 1; date <= totalDays; date++) {
          const currentDate = new Date(year, monthIndex, date);
          const dayName = days[currentDate.getDay()];
          const week = Math.ceil(date / 7);
          const quarter = getQuarter(monthIndex);

          Object.entries(categories).forEach(([category, products]) => {
            products.forEach((product, productIndex) => {
              const base =
                year * 3 +
                (monthIndex + 1) * 850 +
                date * 95 +
                productIndex * 550 +
                category.length * 100;

              const appWebsiteSales = base % 9000 + 2500;
              const kioskSales = base % 6500 + 1500;
              const posSales = base % 11000 + 3000;
              const totalSales = appWebsiteSales + kioskSales + posSales;

              rows.push({
                year,
                quarter,
                month,
                monthIndex,
                week: `Week ${week}`,
                date: `${String(date).padStart(2, "0")}-${String(monthIndex + 1).padStart(2, "0")}-${year}`,
                day: dayName,
                category,
                product,
                appWebsiteSales,
                kioskSales,
                posSales,
                totalSales,
                orders: Math.floor(totalSales / 650),
                quantity: Math.floor(totalSales / 420)
              });
            });
          });
        }
      });
    }

    return rows;
  }, []);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return dummyRows;

    const s = search.toLowerCase();

    return dummyRows.filter((row) =>
      [
        row.year,
        row.quarter,
        row.month,
        row.week,
        row.date,
        row.day,
        row.category,
        row.product
      ]
        .join(" ")
        .toLowerCase()
        .includes(s)
    );
  }, [dummyRows, search]);

  const totalOf = (rows, key) =>
    rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);

  const groupBy = (rows, key) => {
    return rows.reduce((acc, row) => {
      const value = row[key];
      if (!acc[value]) acc[value] = [];
      acc[value].push(row);
      return acc;
    }, {});
  };

  const renderSummaryCells = (rows) => (
    <>
      <td className="text-end">{totalOf(rows, "orders").toLocaleString("en-IN")}</td>
      <td className="text-end">{totalOf(rows, "quantity").toLocaleString("en-IN")}</td>
      <td className="text-end">{formatCurrency(totalOf(rows, "appWebsiteSales"))}</td>
      <td className="text-end">{formatCurrency(totalOf(rows, "kioskSales"))}</td>
      <td className="text-end">{formatCurrency(totalOf(rows, "posSales"))}</td>
      <td className="text-end fw-bold text-primary">
        {formatCurrency(totalOf(rows, "totalSales"))}
      </td>
    </>
  );

  const renderToggle = (key, label, level, rows, badge = null) => {
    const isOpen = !!expanded[key];

    return (
      <tr className={`matrix-level-${level}`}>
        <td style={{ paddingLeft: `${level * 22}px` }} className="fw-semibold">
          <button
            type="button"
            className="btn btn-sm btn-light border me-2 py-0"
            onClick={() => toggleExpand(key)}
          >
            {isOpen ? "−" : "+"}
          </button>
          {label}
          {badge && (
            <Badge bg="secondary" className="ms-2">
              {badge}
            </Badge>
          )}
        </td>
        {renderSummaryCells(rows)}
      </tr>
    );
  };

  const renderMatrixRows = () => {
    const yearGroups = groupBy(filteredRows, "year");
    const output = [];

    Object.keys(yearGroups)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((year) => {
        const yearRows = yearGroups[year];
        const yearKey = `year-${year}`;

        output.push(renderToggle(yearKey, year, 0, yearRows, "Year"));

        if (!expanded[yearKey]) return;

        const quarterGroups = groupBy(yearRows, "quarter");

        Object.keys(quarterGroups).forEach((quarter) => {
          const quarterRows = quarterGroups[quarter];
          const quarterKey = `${yearKey}-${quarter}`;

          output.push(renderToggle(quarterKey, quarter, 1, quarterRows, "Quarter"));

          if (!expanded[quarterKey]) return;

          const monthGroups = groupBy(quarterRows, "month");

          Object.keys(monthGroups)
            .sort((a, b) => monthGroups[a][0].monthIndex - monthGroups[b][0].monthIndex)
            .forEach((month) => {
              const monthRows = monthGroups[month];
              const monthKey = `${quarterKey}-${month}`;

              output.push(renderToggle(monthKey, month, 2, monthRows, "Month"));

              if (!expanded[monthKey]) return;

              const weekGroups = groupBy(monthRows, "week");

              Object.keys(weekGroups).forEach((week) => {
                const weekRows = weekGroups[week];
                const weekKey = `${monthKey}-${week}`;

                output.push(renderToggle(weekKey, week, 3, weekRows, "Week"));

                if (!expanded[weekKey]) return;

                const dayGroups = groupBy(weekRows, "day");

                Object.keys(dayGroups).forEach((day) => {
                  const dayRows = dayGroups[day];
                  const dayKey = `${weekKey}-${day}`;

                  output.push(renderToggle(dayKey, day, 4, dayRows, "Day"));

                  if (!expanded[dayKey]) return;

                  const categoryGroups = groupBy(dayRows, "category");

                  Object.keys(categoryGroups).forEach((category) => {
                    const categoryRows = categoryGroups[category];
                    const categoryKey = `${dayKey}-${category}`;

                    output.push(renderToggle(categoryKey, category, 5, categoryRows, "Category"));

                    if (!expanded[categoryKey]) return;

                    categoryRows.forEach((productRow, index) => {
                      output.push(
                        <tr key={`${categoryKey}-${productRow.product}-${index}`}>
                          <td style={{ paddingLeft: "150px" }}>
                            <span className="text-muted me-2">└</span>
                            {productRow.product}
                            <small className="text-muted ms-2">({productRow.date})</small>
                          </td>
                          <td className="text-end">{productRow.orders}</td>
                          <td className="text-end">{productRow.quantity}</td>
                          <td className="text-end">{formatCurrency(productRow.appWebsiteSales)}</td>
                          <td className="text-end">{formatCurrency(productRow.kioskSales)}</td>
                          <td className="text-end">{formatCurrency(productRow.posSales)}</td>
                          <td className="text-end fw-semibold text-primary">
                            {formatCurrency(productRow.totalSales)}
                          </td>
                        </tr>
                      );
                    });
                  });
                });
              });
            });
        });
      });

    return output;
  };

  return (
    <>
      <Header onSkin={setSkin} />

      <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0 fw-bold">
                <i className="bi bi-table me-2"></i>
                SALES MATRIX REPORT
              </h4>
              <small className="text-muted">
                Drill-down: Year → Quarter → Month → Week → Day → Category → Product
              </small>
            </div>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate("/reports/all-reports")}
              className="d-flex align-items-center fw-semibold"
            >
              <ArrowLeft className="me-1" /> Back to Reports
            </Button>
          </div>

          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <Form.Control
                placeholder="Search year, quarter, month, weekday, category or product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <Button variant="outline-primary" onClick={() => setExpanded({})}>
                Collapse All
              </Button>
            </div>

            <div className="table-responsive border rounded-3">
              <Table bordered hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: "360px" }}>Matrix Row</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">App / Website</th>
                    <th className="text-end">Kiosk</th>
                    <th className="text-end">POS</th>
                    <th className="text-end text-primary">Total Sales</th>
                  </tr>
                </thead>

                <tbody>
                  {renderMatrixRows()}

                  <tr className="table-dark fw-bold">
                    <td>Grand Total</td>
                    {renderSummaryCells(filteredRows)}
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesMatrixReport;